import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, matchPath, useLocation, useNavigationType } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { HelmetProvider } from 'react-helmet-async';
import AnalyticsTracker from './components/AnalyticsTracker';

import lazyPage from './lazyPage';
import { paths, blogPaths } from './models/paths';

// Pages are split into their own chunks; see preloadRoute() below.
const TenderPage = lazyPage(() => import('./pages/TenderPage'));
const AboutPage = lazyPage(() => import('./pages/AboutPage'));
const ContactPage = lazyPage(() => import('./pages/ContactPage'));
const ServiceSelectorPage = lazyPage(() => import('./pages/ServiceSelectorPage'));
const CommercialPage = lazyPage(() => import('./pages/CommercialPage'));
const DomesticCleaningPage = lazyPage(() => import('./pages/DomesticCleaningPage'));
const DomesticServicesPage = lazyPage(() => import('./pages/DomesticServicesPage'));
const NotFoundPage = lazyPage(() => import('./pages/NotFoundPage'));
const EndOfTenancyCleaningPage = lazyPage(() => import('./pages/EndOfTenancyCleaningPage'));
const DeepCleaningPage = lazyPage(() => import('./pages/DeepCleaningPage'));
const RegularHouseCleaningPage = lazyPage(() => import('./pages/RegularHouseCleaningPage'));
const OvenCarpetCleaningPage = lazyPage(() => import('./pages/OvenCarpetCleaningPage'));
const OfficeCleaningPage = lazyPage(() => import('./pages/OfficeCleaningPage'));
const SchoolCleaningPage = lazyPage(() => import('./pages/SchoolCleaningPage'));
const BlogIndexPage = lazyPage(() => import('./pages/BlogIndexPage'));
const HouseCleaningCostPost = lazyPage(() => import('./pages/blog/HouseCleaningCostPost'));
const EndOfTenancyChecklistPost = lazyPage(() => import('./pages/blog/EndOfTenancyChecklistPost'));
const DeepVsRegularPost = lazyPage(() => import('./pages/blog/DeepVsRegularPost'));
const CommercialCleaningCostPost = lazyPage(() => import('./pages/blog/CommercialCleaningCostPost'));
const OfficeCleaningFrequencyPost = lazyPage(() => import('./pages/blog/OfficeCleaningFrequencyPost'));
const MoveInChecklistPost = lazyPage(() => import('./pages/blog/MoveInChecklistPost'));
const TwoBedroomCleanCostPost = lazyPage(() => import('./pages/blog/TwoBedroomCleanCostPost'));
const AirbnbCleaningChecklistPost = lazyPage(() => import('./pages/blog/AirbnbCleaningChecklistPost'));
const SpringCleaningChecklistPost = lazyPage(() => import('./pages/blog/SpringCleaningChecklistPost'));
const PrepareForCleanerPost = lazyPage(() => import('./pages/blog/PrepareForCleanerPost'));
const HowOftenToCleanPost = lazyPage(() => import('./pages/blog/HowOftenToCleanPost'));

const TenderRoute = () => <><TenderPage /><Footer /></>;
const ContactRoute = () => <><ContactPage /><Footer /></>;
const NotFoundRoute = () => <><NotFoundPage /><Footer /></>;

// Path -> page component, used to preload the right chunk before hydrating.
// Pages built on src/templates/PageShell.js render their own Footer.
const pageRoutes = [
  { path: paths.home, page: ServiceSelectorPage },
  { path: paths.commercial, page: CommercialPage },
  { path: paths.houseHub, page: DomesticCleaningPage },
  { path: paths.prices, page: DomesticServicesPage },
  { path: paths.endOfTenancy, page: EndOfTenancyCleaningPage },
  { path: paths.deep, page: DeepCleaningPage },
  { path: paths.regular, page: RegularHouseCleaningPage },
  { path: paths.ovenCarpet, page: OvenCarpetCleaningPage },
  { path: paths.office, page: OfficeCleaningPage },
  { path: paths.school, page: SchoolCleaningPage },
  { path: paths.projects, page: TenderPage, route: TenderRoute },
  { path: paths.about, page: AboutPage },
  { path: paths.contact, page: ContactPage, route: ContactRoute },
  { path: paths.blog, page: BlogIndexPage },
  { path: blogPaths.houseCleaningCost, page: HouseCleaningCostPost },
  { path: blogPaths.endOfTenancyChecklist, page: EndOfTenancyChecklistPost },
  { path: blogPaths.deepVsRegular, page: DeepVsRegularPost },
  { path: blogPaths.commercialCost, page: CommercialCleaningCostPost },
  { path: blogPaths.officeFrequency, page: OfficeCleaningFrequencyPost },
  { path: blogPaths.moveInChecklist, page: MoveInChecklistPost },
  { path: blogPaths.twoBedroomCost, page: TwoBedroomCleanCostPost },
  { path: blogPaths.airbnbChecklist, page: AirbnbCleaningChecklistPost },
  { path: blogPaths.springChecklist, page: SpringCleaningChecklistPost },
  { path: blogPaths.prepareForCleaner, page: PrepareForCleanerPost },
  { path: blogPaths.howOftenToClean, page: HowOftenToCleanPost },
];

// Old URLs (content pack section 1). The server 301s them via
// public/_redirects; these cover in-app navigation to a stale link.
const legacyRedirects = [
  ['/domestic-cleaning', paths.houseHub],
  ['/domestic-cleaning/services', paths.prices],
  ['/commercial', paths.commercial],
  ['/services', paths.commercial],
];

export const preloadRoute = (pathname) => {
  const legacy = legacyRedirects.find(([from]) => matchPath(from, pathname));
  const target = legacy ? legacy[1] : pathname;
  const match = pageRoutes.find((route) => matchPath(route.path, target));
  if (match) return match.page.preload();
  return NotFoundPage.preload();
};

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();

  React.useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      const scrollToTarget = () => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      };

      const frameId = requestAnimationFrame(() => {
        scrollToTarget();
        const timeoutId = setTimeout(scrollToTarget, 100);
        return () => clearTimeout(timeoutId);
      });

      return () => cancelAnimationFrame(frameId);
    } else if (navigationType !== 'POP') {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash, navigationType]);

  return null;
};

function App() {
  return (
    <HelmetProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <ScrollToTop />
        <AnalyticsTracker />
        <NavbarWrapper />
        <Suspense fallback={null}>
        {/* display:contents wrapper = layout-neutral anchor that
            scripts/prerender.js wraps in React's Suspense hydration markers */}
        <div data-route-root="" style={{ display: 'contents' }}>
        <Routes>
          {pageRoutes.map(({ path, page: Page, route: PageRoute }) => (
            <Route key={path} path={path} element={PageRoute ? <PageRoute /> : <Page />} />
          ))}
          {legacyRedirects.map(([from, to]) => (
            <Route key={from} path={from} element={<Navigate to={to} replace />} />
          ))}
          <Route path="*" element={<NotFoundRoute />} />
        </Routes>
        </div>
        </Suspense>
      </Router>
    </HelmetProvider>
  );
}

const NavbarWrapper = () => {
  const location = useLocation();
  if (location.pathname === "/") {
    return null;
  }
  return <Navbar />;
};

export default App;
